import { useEffect, useState } from "react";
import { ArrowRight, BookOpen, Clock3, FileCheck2, PenLine, Timer } from "lucide-react";
import { HomeworkShell, useHwAccount } from "./HomeworkShell";
import { hwApi, fmtDate } from "./hwApi";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
export const hwdApi = axios.create({ baseURL: `${API}/api/hwd`, withCredentials: true });
export const tsApi = axios.create({ baseURL: `${API}/api/ts`, withCredentials: true });

const STATUS_LABEL = { draft: "DRAFT", submitted: "WITH SIR", reviewing: "BEING CHECKED", evaluated: "CHECKED",
                       returned_for_correction: "CORRECTIONS", resubmission_requested: "RESUBMIT" };
const STATUS_CLS = { draft: "locked", submitted: "review", reviewing: "review", evaluated: "approved",
                     returned_for_correction: "failed", resubmission_requested: "failed" };

function HomeInner() {
  const account = useHwAccount();
  const [subs, setSubs] = useState(null);
  useEffect(() => {
    hwdApi.get("/submissions").then(({ data }) => setSubs(data.submissions.slice(0, 6))).catch(() => setSubs([]));
  }, []);

  return (
    <div className="tp-wrap" data-testid="hw-home">
      <div className="tp-eyebrow">WELCOME, {account.name.split(" ")[0].toUpperCase()}</div>
      <h1 className="tp-h2" style={{ fontSize: 34 }}>Your Homework. Your Progress. Personal Guidance.</h1>
      <p className="tp-muted" style={{ maxWidth: 560 }}>Choose your class and topic, upload your completed homework, and receive personal evaluation and feedback from Pranjal Sir.</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 18, marginTop: 22 }}>
        <a href="/homework/new" data-testid="entry-homework" style={{ textDecoration: "none" }}>
          <div className="tp-panel hw-entry hw-entry-hw" style={{ height: "100%", border: "2px solid #1d4ed8", background: "linear-gradient(150deg,#eff6ff 0%,#ffffff 60%)", transition: "transform .25s ease, box-shadow .25s ease" }}>
            <div className="tp-ico" style={{ background: "#1d4ed8", color: "#fff" }}><BookOpen size={22} /></div>
            <h2 className="tp-h2" style={{ fontSize: 22, marginTop: 12 }}>Homework &amp; Personal Evaluation</h2>
            <p className="tp-muted" style={{ fontSize: 14 }}>Select your topic, upload your question paper and solved homework, and receive personal feedback from Pranjal Sir.</p>
            <span className="tp-btn" style={{ marginTop: 14, background: "#1d4ed8" }} data-testid="entry-homework-btn">Start Homework <ArrowRight size={15} /></span>
            <div style={{ marginTop: 12, display: "flex", gap: 6, flexWrap: "wrap" }}>
              <span className="status-pill review">Upload question paper</span>
              <span className="status-pill review">Your solved work</span>
              <span className="status-pill review">Checked personally</span>
            </div>
          </div>
        </a>
        <a href="/homework/tests" data-testid="entry-testseries" style={{ textDecoration: "none" }}>
          <div className="tp-panel hw-entry hw-entry-ts" style={{ height: "100%", border: "2px solid #ea580c", background: "linear-gradient(150deg,#fff7ed 0%,#ffffff 60%)", transition: "transform .25s ease, box-shadow .25s ease" }}>
            <div className="tp-ico" style={{ background: "#ea580c", color: "#fff" }}><Timer size={22} /></div>
            <h2 className="tp-h2" style={{ fontSize: 22, marginTop: 12 }}>Test Series by Pranjal sir</h2>
            <p className="tp-muted" style={{ fontSize: 14 }}>Challenge yourself with five-question timed tests and improve your accuracy. Instant scores, separate from homework marks.</p>
            <span className="tp-btn" style={{ marginTop: 14, background: "#ea580c" }} data-testid="entry-test-btn">Start a Test <ArrowRight size={15} /></span>
            <div style={{ marginTop: 12, display: "flex", gap: 6, flexWrap: "wrap" }}>
              <span className="status-pill" style={{ background: "#ffedd5", color: "#9a3412" }}>5 questions</span>
              <span className="status-pill" style={{ background: "#ffedd5", color: "#9a3412" }}>5 or 10 min timer</span>
              <span className="status-pill" style={{ background: "#ffedd5", color: "#9a3412" }}>Instant score</span>
            </div>
          </div>
        </a>
      </div>

      <div className="tp-panel" style={{ marginTop: 22 }} data-testid="hw-recent">
        <div className="admin-row" style={{ justifyContent: "space-between" }}>
          <h2 className="tp-h2" style={{ fontSize: 18, margin: 0 }}>My recent homework</h2>
          <a href="/homework/list" className="tp-back" style={{ textDecoration: "none" }} data-testid="hw-all-link">View all →</a>
        </div>
        {subs === null && <p className="tp-muted">Loading…</p>}
        {subs && subs.length === 0 && (
          <p className="tp-muted" data-testid="hw-recent-empty">No homework submitted yet. Click <b>Start Homework</b> above — upload your question paper and solved work to get personal feedback from Pranjal Sir.</p>
        )}
        {(subs || []).map((s) => (
          <a key={s.submission_id} href={`/homework/hw/${s.submission_id}`} style={{ textDecoration: "none" }} data-testid={`hw-recent-${s.submission_id}`}>
            <div className="admin-row" style={{ borderTop: "1px solid #e4eaf2", padding: "10px 0" }}>
              <span className={`status-pill ${STATUS_CLS[s.status]}`}>{STATUS_LABEL[s.status]}</span>
              <span style={{ flex: 1, color: "#1e3a5f" }}>{s.title} <small className="tp-muted">· {s.category_label} · {s.submitted_at ? fmtDate(s.submitted_at) : "draft"}</small></span>
              {s.evaluation && <b style={{ color: "#15803d" }} data-testid={`hw-recent-score-${s.submission_id}`}>{s.evaluation.score_10}/10</b>}
            </div>
          </a>
        ))}
      </div>

      <div className="admin-row" style={{ marginTop: 18, gap: 14, flexWrap: "wrap" }}>
        <a href="/homework/results" className="tp-back" style={{ textDecoration: "none" }} data-testid="hw-legacy-results">Earlier question-bank results →</a>
        <a href="/homework/payments" className="tp-back" style={{ textDecoration: "none" }} data-testid="hw-store-link">Mentor program &amp; payments →</a>
      </div>
    </div>
  );
}

export default function HomeworkHome() {
  return <HomeworkShell><HomeInner /></HomeworkShell>;
}
